'use strict';

angular.module('ClinicApp').directive(
  'userPrimeAppointments',
  (
    $q,
    $timeout,
    PrimeInvoiceService,
    OCModalUtil,
    $filter,
    $state,
    BillingContestedConstants,
    MonthOptionsConstants,
    AppointmentService,
    GrowlService,
    CurrentUser,
    OCPeriodMapUtil,
    OCClinicUtil,
    OpencareConstants,
    OCAppointmentUtil,
    ClinicConfig,
    OCUtil,
    OCLogUtil,
  ) => ({
    restrict: 'A',
    templateUrl: '/views/directives/_user-prime-appointments.html',
    link: ($scope) => {
      const clinics = CurrentUser.getClinics();
      const providersByClinicId = clinics.reduce((result, clinic) => {
        result[clinic.id] = clinic.providers.map((provider) => {
          provider.doctor = CurrentUser.getDoctors().find((doctor) => doctor.id === provider.doctor);
          return provider;
        }).filter((provider) => provider.doctor?.id);
        return result;
      }, {});

      $scope.clinics = _.sortBy(clinics, (clinic) => clinic.name);
      $scope.providers = _.sortBy(providersByClinicId[$scope.clinics[0].id], (provider) => provider.doctor.name);
      $scope.userSelected = {
        clinic: $scope.clinics[0],
        provider: null,
        period: {
          year: null,
          month: null,
        },
      };
      $scope.billableYears = [];
      $scope.billableMonthsByYear = {};
      $scope.billingSummary = {};
      $scope.billingPolicyUrl = `${ClinicConfig.SEARCH_APP_URL}/billing/`;
      $scope.appointments = [];
      $scope.loading = false;
      $scope.hasError = false;

      // Reset providers when clinic changes.
      $scope.$watch('userSelected.clinic', (selectedClinic) => {
        let providers = [];

        if (Array.isArray(selectedClinic)) {
          // Get all providers from selected clinics.
          providers = selectedClinic.map((clinic) => providersByClinicId[clinic.id]).flat();
          // Remove duplicate doctors.
          providers = providers.reduce((result, provider) => {
            if (!result.find((item) => item.doctor.id === provider.doctor.id)) {
              result.push(provider);
            }
            return result;
          }, []);
        } else if (selectedClinic) {
          providers = providersByClinicId[selectedClinic.id];
        }

        $scope.userSelected.provider = null;
        $scope.providers = _.sortBy(providers, (provider) => provider.doctor.name);
      });

      $scope.updateUserSelected = () => {
        onUpdateUserSelected();
      };

      $scope.isClinicMultiSelect = () => Array.isArray($scope.userSelected.clinic);
      $scope.isPeriodMultiSelect = () => Array.isArray($scope.userSelected.period);

      $scope.appointmentCanBeContested = (appointment) => !appointment?.metadata?.billingContested;
      $scope.appointmentIsContested = (appointment) =>
        appointment?.metadata?.billingContested === BillingContestedConstants.CONTESTED;
      $scope.appointmentIsEditable = (appointment) =>
        appointment?.primeInvoice?.billingStatus === OpencareConstants.billingStates.NOT_BILLED
        && !$scope.appointmentIsContested(appointment);
      $scope.appointmentAutomaticallyVerified = AppointmentService.isAutomaticallyVerified;
      $scope.appointmentIsReconcilable = (appointment) =>
        OCAppointmentUtil.isReconcilable(appointment) && $scope.appointmentIsEditable(appointment);
      $scope.appointmentIsDeferred = OCAppointmentUtil.isDeferred;
      $scope.appointmentIsDeferrable = OCAppointmentUtil.isDeferrable;
      $scope.appointmentIsBillable = (appointment) =>
        appointment.visited && appointment?.metadata?.billingStatus === OpencareConstants.billingStates.TO_BILL;
      $scope.needsVerification = OCAppointmentUtil.needsVerification;
      $scope.appointmentVerified = ({ verified }) => verified;

      $scope.appointmentIsNotBilledGOB = (appointment) => {
        const isCancelled = appointment.status === OpencareConstants.appointmentStates.CANCELLED;
        const isNotBilled = appointment.metadata?.billingStatus === OpencareConstants.billingStates.NOT_BILLED;
        const isGOB = !!appointment.requestData.googleActions?.rwgToken;
        return !isCancelled && isNotBilled && isGOB;
      };

      $scope.getDeferMonthName = (appointment) => getInvoiceDate(appointment.primeInvoice).add(1, 'month')
        .format('MMMM');

      $scope.selectAppointment = (appointment) => {
        const url = $state.href('clinic.appointment', {
          appointmentId: appointment.id,
          id: appointment.provider.clinic.id,
          tab: 'requests',
          page: 1,
        });
        window.open(url, '_blank');
      };

      const isAppointmentCurrent = (appointment) =>
        Boolean(OCAppointmentUtil.isRequested(appointment.status) || OCAppointmentUtil.isScheduled(appointment.status));

      $scope.getAppointmentActionText = (appointment) => {
        if (isAppointmentCurrent(appointment)) {
          return 'View';
        }

        return OCAppointmentUtil.needsVerification(appointment) ? 'Verify' : 'Edit';
      };

      $scope.shouldShowActionCta = (appointment) =>
        Boolean($scope.appointmentIsEditable(appointment) && !$scope.appointmentVerified(appointment));

      $scope.handleActionClicked = (appointment) => {
        if (isAppointmentCurrent(appointment)) {
          $scope.selectAppointment(appointment);
          return;
        }

        if (OCAppointmentUtil.isCancelled(appointment.status) || OCAppointmentUtil.isNoShow(appointment)) {
          openEditOrReopenModal(appointment);
          return;
        }

        openVerifyAppointmentModal(appointment);
      };

      const openEditOrReopenModal = (appointment) => {
        OCModalUtil.showCustomModal({
          windowClass: 'v4-modal',
          templateUrl: '/views/modals/_edit-reopen-appointment.html',
          controller: 'EditReopenAppointmentModalCtrl',
          resolve: OCModalUtil.createResolve({
            appointment,
            clinic: appointment.provider.clinic,
          }),
        }).then(() => {
          onUpdateUserSelected();
        });
      };

      const openVerifyAppointmentModal = (appointment) => {
        OCModalUtil.showCustomModal({
          windowClass: 'verify-appointment-modal',
          templateUrl: '/views/modals/_verify-appointment.html',
          controller: 'VerifyAppointmentModalCtrl',
          resolve: OCModalUtil.createResolve({
            appointmentId: appointment.id,
          }),
        }).then(() => {
          onUpdateUserSelected();
        });
      };

      $scope.openBillingContestedModal = (appointment) => {
        OCModalUtil.showModal('billingContested', {
          appointment,
          refreshAppointments: onUpdateUserSelected,
        });
      };

      $scope.openBillingInstructionsModal = () => {
        OCModalUtil.showCustomModal({
          templateUrl: '/views/modals/_billing-instructions.html',
          controller: 'ModalGenericOk',
        });
      };

      $scope.getCSVHeader = () => [
        'Practice',
        'Provider',
        'Patient',
        'Requested Date',
        'Scheduled Date',
        'Appointment Status',
        'Show-up status',
        'Billing Status',
      ];

      $scope.getCSVData = () => {
        return _.chain($scope.appointments)
          .sortBy((appointment) => -appointment.id)
          .map((appointment) => ({
            Practice: appointment.provider.clinic.name,
            Provider: appointment.provider.doctor.name,
            Patient: `${appointment.patient.firstName} ${appointment.patient.lastName}`,
            'Requested Date': $filter('momentFormat')(appointment.createdAt, $scope.isPeriodMultiSelect() ? 'MMM Do, YYYY' : 'MMM Do'),
            'Scheduled Date': $filter('momentUTCFormat')(appointment.date, $scope.isPeriodMultiSelect() ? 'MMM Do, YYYY' : 'MMM Do'),
            'Appointment Status': $filter('appointmentStatus')(appointment.status),
            'Show-up status': appointment.visited ? 'Success' : appointment.visited === false ? 'No Show' : '-',
            'Billing Status': $filter('appointmentBillingStatus')(appointment.metadata.billingStatus),
          }))
          .value();
      };

      $scope.defer = (appointment) => {
        const nextInvoiceDate = getInvoiceDate(appointment.primeInvoice).add(1, 'month');
        const nextMonth = nextInvoiceDate.format('MMMM');
        const body = `Are you sure you want to defer this appointment to ${nextMonth}?`;

        return OCModalUtil
          .showModal('genericYesNo', {
            title: `Move appointment to ${nextMonth}`,
            body: body,
            btnYes: `Move to ${nextMonth}`,
            btnNo: 'Cancel',
          })
          .then(() => {
            AppointmentService.defer(appointment.id)
              .then(() => {
                onUpdateUserSelected();
                GrowlService.success(`Appointment has been deferred to ${nextMonth}.`);
              });
          });
      };

      function onUpdateUserSelected() {
        if ($scope.loading) {
          return;
        }

        $scope.hasError = false;
        $scope.loading = true;
        $scope.userSelected = $scope.userSelected || {};
        $scope.userSelected.period = $scope.userSelected.period || {};

        let selectedProviders = [];
        let selectedClinics = [];

        if ($scope.userSelected.provider) {
          selectedProviders = Array.isArray($scope.userSelected.provider)
            ? $scope.userSelected.provider
            : [$scope.userSelected.provider];
          selectedProviders = selectedProviders.filter((provider) => provider.id);
        }
        if ($scope.userSelected.clinic) {
          selectedClinics = Array.isArray($scope.userSelected.clinic)
            ? $scope.userSelected.clinic
            : [$scope.userSelected.clinic];
          selectedClinics = selectedClinics.filter((clinic) => clinic.id);
        }

        let billableEntityModelType;
        let billableEntityModelIds;

        if (selectedProviders.length) {
          billableEntityModelType = 'provider';
          const doctorIds = selectedProviders.map((provider) => provider.doctor.id).filter(Boolean);
          billableEntityModelIds = selectedClinics.map((clinic) => {
            return clinic.providers.filter((provider) => doctorIds.includes(provider.doctor?.id));
          }).flat()
            .map((provider) => provider.id);
        } else if (selectedClinics.length) {
          billableEntityModelType = 'clinic';
          billableEntityModelIds = selectedClinics.map((clinic) => clinic.id);
        } else {
          $scope.loading = false;
          return;
        }

        return OCPeriodMapUtil.updateScopeWithPeriodMap($scope, 'billingEntity', billableEntityModelIds, billableEntityModelType).then(() => {
          let selectedPeriods = Array.isArray($scope.userSelected.period)
            ? $scope.userSelected.period
            : [$scope.userSelected.period];
          selectedPeriods = selectedPeriods.filter((period) => period?.year && period?.month);

          if (!selectedPeriods.length) {
            $scope.loading = false;
            return;
          }

          return $q.all(
            billableEntityModelIds.map(
              (billableEntityModelId) =>
                selectedPeriods.map(
                  (period) =>
                    PrimeInvoiceService.getBillableEntity(
                      billableEntityModelType,
                      billableEntityModelId,
                      period.year,
                      period.month,
                    ),
                ),
            ).flat(),
          )
            .then((billableEntities) => {
              $scope.appointments = _.chain(billableEntities)
                .map('appointments')
                .flatten()
                .sortBy((appointment) => moment(appointment.createdAt).valueOf())
                .sortBy((appointment) => {
                  if (OCAppointmentUtil.isRequested(appointment.status)) {
                    return 0;
                  }

                  if (OCAppointmentUtil.isScheduled(appointment.status)) {
                    return 5;
                  }

                  if (OCAppointmentUtil.isCompleted(appointment.status)) {
                    return appointment.visited ? 8 : 12;
                  }

                  return 15;
                })
                .value();

              $scope.verifiableAppointmentsCount = $scope.appointments
                .filter((appointment) => OCAppointmentUtil.needsVerification(appointment)).length;
              $scope.editableAppointmentsCount = $scope.appointments
                .filter((appointment) => $scope.appointmentIsEditable(appointment)).length;

              calculateBillingSummary(billableEntities);

              $scope.hasReservedSlotPromotion = selectedClinics
                .some((clinic) => {
                  return selectedPeriods.some((period) => {
                    const periodMoment = moment().year(period.year)
                      .month(period.month - 1)
                      .startOf('month');
                    return OCClinicUtil.isReservedSlotPromotionEnabled(clinic, periodMoment);
                  });
                });
              $scope.hasAppointmentPatientTier =
                $scope.appointments.some((appointment) => appointment.metadata?.membershipPatientTier);

              $scope.csvFilename = [
                'Prime Billing',
                selectedClinics?.[0]?.name,
                selectedProviders?.[0]?.doctor?.name,
                MonthOptionsConstants[selectedPeriods[0].month],
                selectedPeriods[0].year,
              ].filter(Boolean).join(' ') + '.csv';

              const periodDateString = [
                selectedPeriods[0].year,
                _.padLeft(selectedPeriods[0].month, 2, '0'),
                '01',
              ].join('-');

              OpencareAnalytics.trackPageView({
                pageType: OpencareAnalytics.pageTypes.INVOICE_MANAGER,
                pageName: selectedClinics?.[0]?.name,
                section: 'appointment-list',
                name: 'invoice-manager',
                practiceId: selectedClinics?.[0]?.id,
                providerId: selectedProviders?.[0]?.id,
                content: {
                  'billing-period': periodDateString,
                  is_real_practice: String(!CurrentUser.isImpersonated()),
                },
                userId: CurrentUser.getUser().id,
              });
            });
        })
          .then(() => {
            $scope.hasError = false;
          })
          .catch((error) => {
            $scope.hasError = true;
            GrowlService.error('Error loading appointments');
            OCLogUtil.rollbar.error('Error loading appointments for billing page', error);
          })
          .finally(() => {
            $scope.loading = false;
          });
      }

      function getInvoiceDate(primeInvoice) {
        return moment().year(primeInvoice.year)
          .month(primeInvoice.month - 1);
      }

      /**
       * Calculate the billing summary for appointments and memberships from the provided entities.
       *
       * @param {Array<Object>} billableEntities - An array of objects representing billable entities that contain billing information for appointments and memberships.
       * @return {void} This function does not return a value. It updates the $scope.billingSummary with calculated billing data.
       */
      function calculateBillingSummary(billableEntities) {
        const appointmentsProps = ['subtotal', 'tax', 'total'];
        const membershipProps = ['price', 'discount', 'googleOnlineBookingPrice', 'extraFeesSubTotal', 'subTotal', 'tax', 'total'];

        $scope.billingSummary.appointments = appointmentsProps.reduce((result, prop) => {
          result[prop] = OCUtil.mapSum(billableEntities, prop);
          return result;
        }, {});
        $scope.billingSummary.membership = membershipProps.reduce((result, prop) => {
          result[prop] = OCUtil.mapSum(billableEntities, (entity) => entity.membership?.[prop]);
          return result;
        }, {});
        $scope.billingSummary.subTotal = (
          $scope.billingSummary.appointments.subtotal
          + $scope.billingSummary.membership.subTotal
        );
        $scope.billingSummary.tax = $scope.billingSummary.appointments.tax + $scope.billingSummary.membership.tax;
        $scope.billingSummary.total = $scope.billingSummary.appointments.total + $scope.billingSummary.membership.total;
      }

      $timeout(() => {
        onUpdateUserSelected();
      });
    },
  }),
);
